import secureStorage from '../lib/secureStorage';
import React, { useState, useEffect, useCallback, useRef } from 'react';
import authedFetch from '../lib/authedFetch';
import { useNavigate } from 'react-router-dom';
import {
  TrendingUp, TrendingDown, Minus, DollarSign, Target, Users,
  BarChart3, LineChart, PieChart, ArrowUpRight, ArrowDownRight,
  Loader2, RefreshCw, Filter, ChevronDown, ChevronUp, Info,
  AlertCircle, CheckCircle, Award, Layers, Calendar, Download, FileSpreadsheet, FileText, Eye, EyeOff, BellRing,
  Building2, Workflow
} from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Alert, AlertDescription } from '../components/ui/alert';
import { Switch } from '../components/ui/switch';
import { toast } from 'sonner';
import { useAuth } from '../context/AuthContext';
import {
  LineChart as RechartsLine,
  Line,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  Area,
  AreaChart,
  Cell
} from 'recharts';
import logger from '../lib/logger';
import { PageShell } from '../components/layout/PageShell';
import { PageHero } from '../components/layout/PageHero';
import { ProgramNavigationPanel, ProgramNavBadges } from '../components/program-navigation/ProgramNavigationPanel';
import { PortfolioNarrativeCard } from '../components/program-navigation/PortfolioNarrativeCard';
import { ValuePathwaysTab } from '../components/program-navigation/ValuePathwaysTab';
import { getLightSectionTheme } from '../lib/sectionThemes';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Color constants
const COLORS = {
  positive: '#10B981',  // Green
  negative: '#EF4444',  // Red
  neutral: '#6B7280',   // Gray
  primary: '#6366F1',   // Indigo
  secondary: '#8B5CF6', // Purple
  warning: '#F59E0B',   // Amber
};

const CHART_COLORS = ['#6366F1', '#8B5CF6', '#EC4899', '#F59E0B', '#10B981', '#06B6D4'];

// Helper to format currency
const formatCurrency = (value, currency = 'USD') => {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: currency,
    minimumFractionDigits: 0,
    maximumFractionDigits: 0
  }).format(value);
};

// Helper to get trend icon
const TrendIcon = ({ direction, size = 16 }) => {
  if (direction === 'up') return <TrendingUp size={size} className="text-emerald-500" />;
  if (direction === 'down') return <TrendingDown size={size} className="text-rose-500" />;
  return <Minus size={size} className="text-gray-400" />;
};

// ROI Status Badge
const ROIBadge = ({ roi }) => {
  if (roi >= 100) return <Badge className="bg-emerald-100 text-emerald-700">Excellent ROI</Badge>;
  if (roi >= 50) return <Badge className="bg-blue-100 text-blue-700">Good ROI</Badge>;
  if (roi >= 0) return <Badge className="bg-amber-100 text-amber-700">Break-even</Badge>;
  return <Badge className="bg-rose-100 text-rose-700">Negative ROI</Badge>;
};

// Program ROI Card Component
const ProgramROICard = ({ program, onViewDetails, rank }) => {
  const getRankBadge = (rank) => {
    if (rank === 1) return <Badge className="bg-amber-100 text-amber-700">🥇 Top Performer</Badge>;
    if (rank === 2) return <Badge className="bg-gray-100 text-gray-600">🥈 Runner-up</Badge>;
    if (rank === 3) return <Badge className="bg-amber-50 text-amber-600">🥉 3rd Place</Badge>;
    return null;
  };
  
  // Check if this is demo data (program name starts with [Demo])
  const isDemo = program.program_name?.startsWith('[Demo]');

  return (
    <Card 
      className={`hover:shadow-lg transition-shadow cursor-pointer border-l-4 ${isDemo ? 'bg-gradient-to-r from-amber-50/30 to-white' : ''}`}
      style={{ borderLeftColor: program.roi_percentage >= 0 ? COLORS.positive : COLORS.negative }}
      onClick={() => onViewDetails(program.program_id)}
      data-testid={`program-roi-card-${program.program_id}`}
    >
      <CardHeader className="pb-2">
        <div className="flex items-start justify-between">
          <div>
            <div className="flex items-center gap-2">
              <CardTitle className="text-lg">{program.program_name}</CardTitle>
              {isDemo && (
                <Badge className="bg-amber-100 text-amber-700 border-amber-300 text-xs">
                  Sample
                </Badge>
              )}
              {program.organization_name && !isDemo && (
                <Badge variant="outline" className="text-xs text-teal-600 border-teal-200">
                  {program.organization_name}
                </Badge>
              )}
            </div>
            <CardDescription className="flex items-center gap-2 mt-1">
              <Badge variant="outline" className="text-xs">{program.program_type}</Badge>
              <Badge variant="outline" className={
                program.status === 'active' ? 'bg-emerald-50 text-emerald-700' :
                program.status === 'completed' ? 'bg-blue-50 text-blue-700' :
                'bg-gray-50 text-gray-600'
              }>
                {program.status}
              </Badge>
            </CardDescription>
            <div className="mt-2">
              <ProgramNavBadges program={program} />
            </div>
          </div>
          {getRankBadge(rank)}
        </div>
      </CardHeader>
      <CardContent>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          {/* ROI */}
          <div className="text-center p-3 bg-slate-50 rounded-lg">
            <p className="text-xs text-gray-500 uppercase tracking-wide">ROI</p>
            <p className={`text-2xl font-bold ${program.roi_percentage >= 0 ? 'text-emerald-600' : 'text-rose-600'}`}>
              {program.roi_percentage > 0 ? '+' : ''}{(program.roi_percentage / 100).toFixed(2)}x
            </p>
          </div>
          
          {/* Investment */}
          <div className="text-center p-3 bg-slate-50 rounded-lg">
            <p className="text-xs text-gray-500 uppercase tracking-wide">Investment</p>
            <p className="text-lg font-semibold text-gray-900">
              {formatCurrency(program.total_investment)}
            </p>
          </div>
          
          {/* Impact */}
          <div className="text-center p-3 bg-slate-50 rounded-lg">
            <p className="text-xs text-gray-500 uppercase tracking-wide">Impact</p>
            <p className="text-lg font-semibold text-gray-900">
              {formatCurrency(program.total_impact)}
            </p>
          </div>
          
          {/* Net Benefit */}
          <div className="text-center p-3 bg-slate-50 rounded-lg">
            <p className="text-xs text-gray-500 uppercase tracking-wide">Net Benefit</p>
            <p className={`text-lg font-semibold ${program.net_benefit >= 0 ? 'text-emerald-600' : 'text-rose-600'}`}>
              {program.net_benefit >= 0 ? '+' : ''}{formatCurrency(program.net_benefit)}
            </p>
          </div>
        </div>
        
        {/* Navigation Conditions — Missing Middle journey (replaces LEI bar) */}
        <ProgramNavigationPanel program={program} />
        
        <div className="mt-3 flex items-center justify-between text-xs text-gray-400">
          <span>{program.data_points} data points</span>
          <span className="flex items-center gap-1">
            <Users size={12} />
            {program.cohorts?.length || 0} cohorts
          </span>
        </div>
      </CardContent>
    </Card>
  );
};

// Cohort Trend Card Component
const CohortTrendCard = ({ cohort, onViewDetails, onDrillDown }) => {
  const [showDrillDown, setShowDrillDown] = useState(false);
  const [selectedPeriodIndex, setSelectedPeriodIndex] = useState(null); // null = latest
  const { summary } = cohort;
  const leiTrend = summary?.lei_trend || {};
  const kpiTrend = summary?.kpi_trend || {};
  
  // Check if this is demo data
  const isDemo = cohort.cohort_name?.startsWith('[Demo]');
  
  // Prepare chart data with normalized scores
  const chartData = cohort.trend_data?.map(point => ({
    period: point.period,
    lei: point.lei_score,
    kpi: point.kpi_score || point.kpi_value,
    leiChange: point.lei_pct_change,
    kpiChange: point.kpi_pct_change
  })) || [];

  // Get available periods for dropdown
  const availablePeriods = cohort.trend_data?.map((point, idx) => ({
    index: idx,
    period: point.period,
    hasKpiData: point.kpi_component_breakdown?.length > 0,
    hasLeiData: point.lei_pillar_breakdown?.length > 0
  })).filter(p => p.hasKpiData || p.hasLeiData) || [];

  // Get selected period data (default to latest)
  const selectedPeriod = selectedPeriodIndex !== null 
    ? cohort.trend_data?.[selectedPeriodIndex]
    : cohort.trend_data?.[cohort.trend_data.length - 1];
  
  // Deduplicate KPI breakdown - keep only one entry per KPI type (latest/best score)
  const getDeduplicatedKpiBreakdown = (kpiBreakdown) => {
    if (!kpiBreakdown?.length) return [];
    
    const kpiMap = new Map();
    kpiBreakdown.forEach(kpi => {
      const key = kpi.kpi_id || kpi.kpi_name;
      // Keep the one with higher normalized score, or the first one if scores are equal
      if (!kpiMap.has(key) || (kpi.normalized_score > kpiMap.get(key).normalized_score)) {
        kpiMap.set(key, kpi);
      }
    });
    
    return Array.from(kpiMap.values());
  };
  
  const deduplicatedKpiBreakdown = getDeduplicatedKpiBreakdown(selectedPeriod?.kpi_component_breakdown);

  return (
    <Card 
      className={`hover:shadow-lg transition-shadow ${isDemo ? 'bg-gradient-to-r from-amber-50/30 to-white' : ''}`}
      data-testid={`cohort-trend-card-${cohort.cohort_id}`}
    >
      <CardHeader className="pb-2">
        <div className="flex items-start justify-between">
          <div>
            <CardTitle className="text-lg flex items-center gap-2">
              {cohort.cohort_name}
              <TrendIcon direction={leiTrend.trend_direction} />
              {isDemo && (
                <Badge className="bg-amber-100 text-amber-700 border-amber-300 text-xs">
                  Sample
                </Badge>
              )}
              {cohort.organization_name && !isDemo && (
                <Badge variant="outline" className="text-xs text-teal-600 border-teal-200">
                  {cohort.organization_name}
                </Badge>
              )}
            </CardTitle>
            <CardDescription>{cohort.description || `${cohort.cohort_type} cohort`}</CardDescription>
          </div>
          <Badge variant="outline" className="text-xs">
            {cohort.cohort_type}
          </Badge>
        </div>
      </CardHeader>
      <CardContent>
        {/* Mini Trend Chart */}
        {chartData.length > 0 ? (
          <div className="h-32 mb-4">
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart data={chartData}>
                <defs>
                  <linearGradient id={`gradient-${cohort.cohort_id}`} x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor={COLORS.primary} stopOpacity={0.3} />
                    <stop offset="95%" stopColor={COLORS.primary} stopOpacity={0} />
                  </linearGradient>
                  <linearGradient id={`gradient-kpi-${cohort.cohort_id}`} x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor={COLORS.secondary} stopOpacity={0.3} />
                    <stop offset="95%" stopColor={COLORS.secondary} stopOpacity={0} />
                  </linearGradient>
                </defs>
                <Area
                  type="monotone"
                  dataKey="lei"
                  stroke={COLORS.primary}
                  fill={`url(#gradient-${cohort.cohort_id})`}
                  strokeWidth={2}
                  name="LEI"
                />
                {chartData.some(d => d.kpi) && (
                  <Area
                    type="monotone"
                    dataKey="kpi"
                    stroke={COLORS.secondary}
                    fill={`url(#gradient-kpi-${cohort.cohort_id})`}
                    strokeWidth={2}
                    name="KPI"
                  />
                )}
                <Tooltip
                  contentStyle={{ fontSize: '12px', padding: '8px 12px', borderRadius: '8px' }}
                  formatter={(value, name) => [value?.toFixed(1) + '/100', name === 'lei' ? 'LEI Score' : 'KPI Score']}
                />
              </AreaChart>
            </ResponsiveContainer>
          </div>
        ) : (
          <div className="h-32 flex items-center justify-center bg-gray-50 rounded-lg mb-4">
            <p className="text-gray-400 text-sm">No trend data available</p>
          </div>
        )}
        
        {/* Normalized Score Cards with % Change */}
        <div className="grid grid-cols-2 gap-3">
          {/* LEI Normalized Score */}
          <div className="p-3 bg-indigo-50 rounded-lg">
            <p className="text-xs text-indigo-600 uppercase tracking-wide mb-1">LEI Score</p>
            <div className="flex flex-col">
              <div className="flex items-baseline gap-2">
                <span className="text-2xl font-bold text-indigo-900">
                  {leiTrend.end_score?.toFixed(1) || '—'}
                </span>
                <span className="text-xs text-indigo-500">/100</span>
              </div>
              {leiTrend.overall_pct_change !== undefined && leiTrend.overall_pct_change !== 0 && (
                <div className={`text-sm flex items-center gap-1 mt-1 ${leiTrend.overall_pct_change >= 0 ? 'text-emerald-600' : 'text-rose-600'}`}>
                  <TrendIcon direction={leiTrend.trend_direction} size={14} />
                  <span className="font-semibold">
                    {leiTrend.overall_pct_change > 0 ? '+' : ''}{leiTrend.overall_pct_change}%
                  </span>
                  <span className="text-xs text-gray-500">from baseline</span>
                </div>
              )}
            </div>
          </div>
          
          {/* KPI Normalized Score */}
          <div className="p-3 bg-purple-50 rounded-lg">
            <p className="text-xs text-purple-600 uppercase tracking-wide mb-1">KPI Score</p>
            <div className="flex flex-col">
              <div className="flex items-baseline gap-2">
                <span className="text-2xl font-bold text-purple-900">
                  {kpiTrend.end_score?.toFixed(1) || '—'}
                </span>
                <span className="text-xs text-purple-500">/100</span>
              </div>
              {kpiTrend.overall_pct_change !== undefined && kpiTrend.overall_pct_change !== 0 && (
                <div className={`text-sm flex items-center gap-1 mt-1 ${kpiTrend.overall_pct_change >= 0 ? 'text-emerald-600' : 'text-rose-600'}`}>
                  <TrendIcon direction={kpiTrend.trend_direction} size={14} />
                  <span className="font-semibold">
                    {kpiTrend.overall_pct_change > 0 ? '+' : ''}{kpiTrend.overall_pct_change}%
                  </span>
                  <span className="text-xs text-gray-500">from baseline</span>
                </div>
              )}
            </div>
          </div>
        </div>
        
        {/* Drill-Down Toggle */}
        <button
          onClick={(e) => {
            e.stopPropagation();
            setShowDrillDown(!showDrillDown);
          }}
          className="mt-3 w-full flex items-center justify-center gap-2 text-sm text-gray-600 hover:text-indigo-600 py-2 border-t border-gray-100"
          data-testid={`drill-down-toggle-${cohort.cohort_id}`}
        >
          {showDrillDown ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
          {showDrillDown ? 'Hide Details' : 'View Component Breakdown'}
        </button>
        
        {/* Component Breakdown (Drill-Down) */}
        {showDrillDown && (
          <div className="mt-3 space-y-4 animate-in slide-in-from-top-2 duration-200">
            {/* Period Selector */}
            {availablePeriods.length > 1 && (
              <div className="flex items-center justify-between p-2 bg-slate-50 rounded-lg">
                <span className="text-xs font-medium text-gray-600">Viewing data from:</span>
                <select
                  value={selectedPeriodIndex ?? cohort.trend_data.length - 1}
                  onChange={(e) => {
                    const val = parseInt(e.target.value);
                    setSelectedPeriodIndex(val === cohort.trend_data.length - 1 ? null : val);
                  }}
                  className="text-sm border border-gray-200 rounded-md px-2 py-1 bg-white focus:outline-none focus:ring-2 focus:ring-indigo-500"
                  data-testid={`period-selector-${cohort.cohort_id}`}
                >
                  {availablePeriods.map((p) => (
                    <option key={p.index} value={p.index}>
                      {p.period} {p.index === cohort.trend_data.length - 1 ? '(Latest)' : ''}
                    </option>
                  ))}
                </select>
              </div>
            )}
            
            {/* Single Period Indicator (when only one period available) */}
            {availablePeriods.length === 1 && (
              <div className="flex items-center gap-2 p-2 bg-slate-50 rounded-lg">
                <Calendar size={14} className="text-gray-500" />
                <span className="text-xs text-gray-600">Data from: <span className="font-medium">{selectedPeriod?.period}</span></span>
              </div>
            )}
          
            {/* LEI Pillar Breakdown */}
            {selectedPeriod?.lei_pillar_breakdown?.length > 0 && (
              <div className="p-3 bg-gray-50 rounded-lg">
                <p className="text-xs font-semibold text-gray-700 uppercase tracking-wide mb-2 flex items-center gap-1">
                  <Layers size={12} />
                  LEI Pillar Breakdown
                </p>
                <div className="space-y-2">
                  {selectedPeriod.lei_pillar_breakdown.map((pillar, idx) => (
                    <div key={pillar.pillar_name || `pillar-${idx}`} className="flex items-center justify-between text-sm">
                      <span className="text-gray-600 truncate mr-2">{pillar.pillar_name}</span>
                      <div className="flex items-center gap-2">
                        <div className="w-20 h-2 bg-gray-200 rounded-full overflow-hidden">
                          <div 
                            className="h-full bg-indigo-500 rounded-full transition-all"
                            style={{ width: `${Math.min(pillar.score, 100)}%` }}
                          />
                        </div>
                        <span className="font-semibold text-indigo-700 w-12 text-right">
                          {pillar.score?.toFixed(1)}
                        </span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
            
            {/* KPI Component Breakdown - Deduplicated */}
            {deduplicatedKpiBreakdown.length > 0 && (
              <div className="p-3 bg-gray-50 rounded-lg">
                <div className="flex items-center justify-between mb-2">
                  <p className="text-xs font-semibold text-gray-700 uppercase tracking-wide flex items-center gap-1">
                    <Target size={12} />
                    KPI Component Breakdown
                  </p>
                  <span className="text-xs text-gray-400">{deduplicatedKpiBreakdown.length} metrics</span>
                </div>
                <div className="space-y-2">
                  {deduplicatedKpiBreakdown.map((kpi, idx) => (
                    <div key={kpi.kpi_id || idx} className="flex items-center justify-between text-sm">
                      <div className="flex flex-col flex-1 min-w-0">
                        <span className="text-gray-600 truncate">{kpi.kpi_name}</span>
                        <span className="text-xs text-gray-400">
                          {kpi.raw_value} {kpi.unit} (target: {kpi.target} {kpi.unit})
                        </span>
                      </div>
                      <div className="flex items-center gap-2 ml-2">
                        <div className="w-16 h-2 bg-gray-200 rounded-full overflow-hidden">
                          <div 
                            className={`h-full rounded-full transition-all ${
                              kpi.performance === 'above_target' ? 'bg-emerald-500' :
                              kpi.performance === 'on_track' ? 'bg-blue-500' :
                              'bg-amber-500'
                            }`}
                            style={{ width: `${Math.min(kpi.normalized_score || 0, 100)}%` }}
                          />
                        </div>
                        <Badge 
                          className={`text-xs min-w-[32px] justify-center ${
                            kpi.performance === 'above_target' ? 'bg-emerald-100 text-emerald-700' :
                            kpi.performance === 'on_track' ? 'bg-blue-100 text-blue-700' :
                            'bg-amber-100 text-amber-700'
                          }`}
                        >
                          {kpi.normalized_score?.toFixed(0)}
                        </Badge>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
            
            {/* No breakdown data message */}
            {(!selectedPeriod?.lei_pillar_breakdown?.length && !deduplicatedKpiBreakdown.length) && (
              <div className="p-3 bg-gray-50 rounded-lg text-center text-sm text-gray-500">
                No component breakdown available for this period
              </div>
            )}
          </div>
        )}
        
        <div className="mt-3 flex items-center justify-between text-xs text-gray-400">
          <span>{summary?.periods_analyzed || 0} periods</span>
          <span className="flex items-center gap-1">
            <Calendar size={12} />
            Baseline: {summary?.baseline_period || 'N/A'}
          </span>
        </div>
      </CardContent>
    </Card>
  );
};

// Period Comparison Card Component
const PeriodComparisonCard = ({ data }) => {
  const [expanded, setExpanded] = useState(false);
  const { comparison, current_period, previous_period, pillar_comparison, kpi_comparison } = data;
  
  const getDeltaColor = (direction) => {
    if (direction === 'improved') return 'text-emerald-600';
    if (direction === 'declined') return 'text-rose-600';
    return 'text-gray-500';
  };
  
  const getDeltaIcon = (direction) => {
    if (direction === 'improved') return <TrendingUp size={16} className="text-emerald-500" />;
    if (direction === 'declined') return <TrendingDown size={16} className="text-rose-500" />;
    return <Minus size={16} className="text-gray-400" />;
  };
  
  const getDeltaBadgeClass = (direction) => {
    if (direction === 'improved') return 'bg-emerald-100 text-emerald-700 border-emerald-200';
    if (direction === 'declined') return 'bg-rose-100 text-rose-700 border-rose-200';
    return 'bg-gray-100 text-gray-600 border-gray-200';
  };

  return (
    <Card 
      className={`transition-all ${data.is_demo ? 'bg-gradient-to-r from-amber-50/30 to-white' : ''}`}
      data-testid={`period-comparison-card-${data.cohort_id}`}
    >
      <CardHeader className="pb-2">
        <div className="flex items-start justify-between">
          <div>
            <CardTitle className="text-lg flex items-center gap-2">
              {data.cohort_name}
              {getDeltaIcon(comparison.lei_direction)}
              {data.is_demo && (
                <Badge className="bg-amber-100 text-amber-700 border-amber-300 text-xs">
                  Sample
                </Badge>
              )}
            </CardTitle>
            <CardDescription>{data.cohort_type} cohort</CardDescription>
          </div>
        </div>
      </CardHeader>
      <CardContent>
        {/* Score Comparison Grid */}
        <div className="grid grid-cols-2 gap-4 mb-4">
          {/* LEI Comparison */}
          <div className="space-y-2">
            <p className="text-xs font-semibold text-indigo-600 uppercase">LEI Score</p>
            <div className="flex items-center gap-2">
              {/* Previous */}
              <div className="flex-1 p-2 bg-gray-50 rounded-lg text-center">
                <p className="text-xs text-gray-500">{previous_period.period}</p>
                <p className="text-lg font-bold text-gray-700">
                  {previous_period.lei_score?.toFixed(1) || '—'}
                </p>
              </div>
              {/* Arrow */}
              <ArrowUpRight className={`${getDeltaColor(comparison.lei_direction)} flex-shrink-0`} size={20} />
              {/* Current */}
              <div className="flex-1 p-2 bg-indigo-50 rounded-lg text-center">
                <p className="text-xs text-indigo-500">{current_period.period}</p>
                <p className="text-lg font-bold text-indigo-700">
                  {current_period.lei_score?.toFixed(1) || '—'}
                </p>
              </div>
            </div>
            {/* Delta Badge */}
            {comparison.lei_delta !== null && (
              <div className="flex justify-center">
                <Badge className={`${getDeltaBadgeClass(comparison.lei_direction)} px-3`}>
                  {comparison.lei_delta > 0 ? '+' : ''}{comparison.lei_delta} pts
                  ({comparison.lei_delta_pct > 0 ? '+' : ''}{comparison.lei_delta_pct}%)
                </Badge>
              </div>
            )}
          </div>
          
          {/* KPI Comparison */}
          <div className="space-y-2">
            <p className="text-xs font-semibold text-purple-600 uppercase">KPI Score</p>
            <div className="flex items-center gap-2">
              {/* Previous */}
              <div className="flex-1 p-2 bg-gray-50 rounded-lg text-center">
                <p className="text-xs text-gray-500">{previous_period.period}</p>
                <p className="text-lg font-bold text-gray-700">
                  {previous_period.kpi_score?.toFixed(1) || '—'}
                </p>
              </div>
              {/* Arrow */}
              <ArrowUpRight className={`${getDeltaColor(comparison.kpi_direction)} flex-shrink-0`} size={20} />
              {/* Current */}
              <div className="flex-1 p-2 bg-purple-50 rounded-lg text-center">
                <p className="text-xs text-purple-500">{current_period.period}</p>
                <p className="text-lg font-bold text-purple-700">
                  {current_period.kpi_score?.toFixed(1) || '—'}
                </p>
              </div>
            </div>
            {/* Delta Badge */}
            {comparison.kpi_delta !== null && (
              <div className="flex justify-center">
                <Badge className={`${getDeltaBadgeClass(comparison.kpi_direction)} px-3`}>
                  {comparison.kpi_delta > 0 ? '+' : ''}{comparison.kpi_delta} pts
                  ({comparison.kpi_delta_pct > 0 ? '+' : ''}{comparison.kpi_delta_pct}%)
                </Badge>
              </div>
            )}
          </div>
        </div>
        
        {/* Expand/Collapse for Pillar & KPI Breakdown */}
        <button
          onClick={() => setExpanded(!expanded)}
          className="w-full flex items-center justify-center gap-2 text-sm text-gray-600 hover:text-indigo-600 py-2 border-t border-gray-100"
          data-testid={`expand-comparison-${data.cohort_id}`}
        >
          {expanded ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
          {expanded ? 'Hide Breakdown' : 'View Breakdown'}
        </button>
        
        {expanded && (
          <div className="mt-3 space-y-4 animate-in slide-in-from-top-2 duration-200">
            {/* Pillar Comparison */}
            {pillar_comparison?.length > 0 && (
              <div className="p-3 bg-gray-50 rounded-lg">
                <p className="text-xs font-semibold text-gray-700 uppercase tracking-wide mb-2 flex items-center gap-1">
                  <Layers size={12} />
                  LEI Pillar Changes
                </p>
                <div className="space-y-2">
                  {pillar_comparison.map((pillar, idx) => (
                    <div key={pillar.pillar_name || `pcomp-${idx}`} className="flex items-center justify-between text-sm">
                      <span className="text-gray-600 truncate mr-2 flex-1">{pillar.pillar_name}</span>
                      <div className="flex items-center gap-2">
                        <span className="text-gray-400 w-12 text-right">{pillar.previous_score?.toFixed(1) || '—'}</span>
                        {getDeltaIcon(pillar.direction)}
                        <span className="font-semibold text-gray-700 w-12 text-right">{pillar.current_score?.toFixed(1) || '—'}</span>
                        {pillar.delta !== null && (
                          <Badge className={`${getDeltaBadgeClass(pillar.direction)} text-xs ml-1`}>
                            {pillar.delta > 0 ? '+' : ''}{pillar.delta}
                          </Badge>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
            
            {/* KPI Component Comparison */}
            {kpi_comparison?.length > 0 && (
              <div className="p-3 bg-gray-50 rounded-lg">
                <p className="text-xs font-semibold text-gray-700 uppercase tracking-wide mb-2 flex items-center gap-1">
                  <Target size={12} />
                  KPI Component Changes
                </p>
                <div className="space-y-2">
                  {kpi_comparison.map((kpi, idx) => (
                    <div key={kpi.kpi_name || `kcomp-${idx}`} className="flex items-center justify-between text-sm">
                      <span className="text-gray-600 truncate mr-2 flex-1">{kpi.kpi_name}</span>
                      <div className="flex items-center gap-2">
                        <span className="text-gray-400 w-12 text-right">{kpi.previous_score?.toFixed(1) || '—'}</span>
                        {getDeltaIcon(kpi.direction)}
                        <span className="font-semibold text-gray-700 w-12 text-right">{kpi.current_score?.toFixed(1) || '—'}</span>
                        {kpi.delta !== null && (
                          <Badge className={`${getDeltaBadgeClass(kpi.direction)} text-xs ml-1`}>
                            {kpi.delta > 0 ? '+' : ''}{kpi.delta}
                          </Badge>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
            
            {(!pillar_comparison?.length && !kpi_comparison?.length) && (
              <div className="p-3 bg-gray-50 rounded-lg text-center text-sm text-gray-500">
                No component breakdown available for comparison
              </div>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  );
};

// Main Page Component
export default function ProgramAnalyticsPage() {
  const navigate = useNavigate();
  const { isSuperAdmin, viewAsOrg } = useAuth();
  
  // State
  const [activeTab, setActiveTab] = useState('program-roi');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  
  // Demo data toggle - persist preference in localStorage
  const [showDemoData, setShowDemoData] = useState(() => {
    const saved = localStorage.getItem('showDemoData');
    return saved !== null ? JSON.parse(saved) : true;
  });
  
  // Organization filter
  const [organizations, setOrganizations] = useState([]);
  const [selectedOrg, setSelectedOrg] = useState(viewAsOrg?.id || 'all');
  const [orgROIData, setOrgROIData] = useState(null);
  const [orgROILoading, setOrgROILoading] = useState(false);

  // When the Super Admin is "Previewing as <Org>", force every analytics
  // query through that org and lock the dropdown. Otherwise the page
  // would silently keep showing all-org rollups (Total Programs: 20)
  // even though the orange preview banner says "scoped to Acme".
  useEffect(() => {
    if (viewAsOrg?.id) {
      setSelectedOrg(viewAsOrg.id);
    } else if (selectedOrg && selectedOrg !== 'all'
               && !organizations.some((o) => o.id === selectedOrg)) {
      // The Super Admin exited preview — fall back to "All Organizations".
      setSelectedOrg('all');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [viewAsOrg?.id]);
  
  // Program ROI State
  const [programROIData, setProgramROIData] = useState(null);
  const [selectedProgramId, setSelectedProgramId] = useState('all');
  // Deep-link (?program_id=) from the Learning Health Trends event
  // markers — consumed once after the org settles.
  const deepLinkProgramRef = useRef(new URLSearchParams(window.location.search).get('program_id'));
  const roiReqIdRef = useRef(0);
  
  // Cohort Trends State
  const [cohortTrendsData, setCohortTrendsData] = useState(null);
  const [selectedCohortId, setSelectedCohortId] = useState('all');
  // Secondary "Filter cohorts by Organization" dropdown — keeps the
  // Cohort dropdown manageable when the rollup spans many tenants.
  const [cohortOrgFilter, setCohortOrgFilter] = useState('all');
  const [metricType, setMetricType] = useState('both');
  const [periodCount, setPeriodCount] = useState(12);
  
  // Period Comparison State
  const [periodComparisonData, setPeriodComparisonData] = useState(null);
  const [comparisonType, setComparisonType] = useState('quarter');
  const [comparisonLoading, setComparisonLoading] = useState(false);
  
  // Fetch organizations (Super Admin only)
  useEffect(() => {
    if (!isSuperAdmin) return;
    const fetchOrgs = async () => {
      const token = secureStorage.getItem('session_token');
      try {
        const res = await authedFetch(`${API_URL}/api/rbac/organizations`, { headers: { 'Authorization': `Bearer ${token}` } });
        if (res.ok) {
          const data = await res.json();
          setOrganizations(data.organizations || []);
        }
      } catch (e) { logger.error('Error fetching orgs:', e); }
    };
    fetchOrgs();
  }, [isSuperAdmin]);
  
  // Reset program/cohort selection when org changes
  useEffect(() => {
    setSelectedProgramId('all');
    setSelectedCohortId('all');
    // Also reset the secondary cohort-org filter so it stays in sync.
    setCohortOrgFilter('all');
  }, [selectedOrg]);

  // Apply a ?program_id= deep-link (from Learning Health Trends event
  // markers) once program data has loaded — runs after the org-reset
  // above so it wins, and consumes the ref so it only applies once.
  useEffect(() => {
    if (deepLinkProgramRef.current && programROIData) {
      setSelectedProgramId(deepLinkProgramRef.current);
      deepLinkProgramRef.current = null;
    }
  }, [programROIData]);

  // Reset selected cohort when the cohort-org filter narrows the list.
  useEffect(() => {
    setSelectedCohortId('all');
  }, [cohortOrgFilter]);
  
  // Save demo data preference
  useEffect(() => {
    localStorage.setItem('showDemoData', JSON.stringify(showDemoData));
  }, [showDemoData]);
  
  // Define fetch functions BEFORE useEffects that use them
  const fetchProgramROI = useCallback(async () => {
    const reqId = ++roiReqIdRef.current;
    try {
      const token = secureStorage.getItem('session_token');
      const params = new URLSearchParams();
      if (selectedProgramId && selectedProgramId !== 'all') {
        params.append('program_id', selectedProgramId);
      }
      if (selectedOrg && selectedOrg !== 'all') {
        params.append('organization_id', selectedOrg);
      }
      params.append('include_demo', showDemoData.toString());
      
      const response = await authedFetch(`${API_URL}/api/analytics/program-roi?${params}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (!response.ok) throw new Error('Failed to fetch program ROI data');
      
      const data = await response.json();
      // Ignore stale responses so a rapid filter change (e.g. a
      // ?program_id= deep-link applied right after the initial
      // unfiltered load) doesn't briefly flash all programs.
      if (reqId !== roiReqIdRef.current) return;
      setProgramROIData(data);
    } catch (err) {
      if (reqId !== roiReqIdRef.current) return;
      logger.error('Error fetching program ROI:', err);
      setError(err.message);
    }
  }, [selectedProgramId, selectedOrg, showDemoData]);
  
  const fetchCohortTrends = useCallback(async () => {
    try {
      const token = secureStorage.getItem('session_token');
      const params = new URLSearchParams();
      params.append('metric_type', metricType);
      params.append('period_count', periodCount.toString());
      params.append('include_demo', showDemoData.toString());
      if (selectedCohortId && selectedCohortId !== 'all') {
        params.append('cohort_id', selectedCohortId);
      }
      if (selectedOrg && selectedOrg !== 'all') {
        params.append('organization_id', selectedOrg);
      }
      
      const response = await authedFetch(`${API_URL}/api/analytics/cohort-trends?${params}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (!response.ok) throw new Error('Failed to fetch cohort trends data');
      
      const data = await response.json();
      setCohortTrendsData(data);
    } catch (err) {
      logger.error('Error fetching cohort trends:', err);
      setError(err.message);
    }
  }, [selectedCohortId, selectedOrg, metricType, periodCount, showDemoData]);
  
  const fetchPeriodComparison = useCallback(async () => {
    try {
      setComparisonLoading(true);
      const token = secureStorage.getItem('session_token');
      const params = new URLSearchParams();
      params.append('comparison_type', comparisonType);
      params.append('include_demo', showDemoData.toString());
      if (selectedOrg && selectedOrg !== 'all') {
        params.append('organization_id', selectedOrg);
      }
      
      const response = await authedFetch(`${API_URL}/api/analytics/period-comparison?${params}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (!response.ok) throw new Error('Failed to fetch period comparison data');
      
      const data = await response.json();
      setPeriodComparisonData(data);
    } catch (err) {
      logger.error('Error fetching period comparison:', err);
      setError(err.message);
    } finally {
      setComparisonLoading(false);
    }
  }, [comparisonType, showDemoData, selectedOrg]);
  
  // Fetch period comparison when tab changes or settings change
  useEffect(() => {
    if (activeTab === 'period-comparison') {
      fetchPeriodComparison();
    }
  }, [activeTab, fetchPeriodComparison]);
  
  // Fetch org ROI data
  const fetchOrgROI = useCallback(async () => {
    setOrgROILoading(true);
    try {
      const token = secureStorage.getItem('session_token');
      const params = new URLSearchParams();
      params.append('include_demo', showDemoData.toString());
      
      const response = await authedFetch(`${API_URL}/api/analytics/organization-roi?${params}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (!response.ok) throw new Error('Failed to fetch organization ROI');
      const data = await response.json();
      setOrgROIData(data);
    } catch (err) {
      logger.error('Error fetching org ROI:', err);
    } finally {
      setOrgROILoading(false);
    }
  }, [showDemoData]);
  
  useEffect(() => {
    if (activeTab === 'org-roi') {
      fetchOrgROI();
    }
  }, [activeTab, fetchOrgROI]);
  
  // fetchData depends on fetchProgramROI and fetchCohortTrends, so define it after them
  const fetchData = useCallback(async () => {
    setLoading(true);
    await Promise.all([fetchProgramROI(), fetchCohortTrends()]);
    setLoading(false);
  }, [fetchProgramROI, fetchCohortTrends]);
  
  // Fetch data on mount and when showDemoData changes
  useEffect(() => {
    fetchData();
  }, [fetchData]);
  
  // Refetch when filters change - split by tab to reduce dependency coupling
  useEffect(() => {
    if (activeTab === 'program-roi') fetchProgramROI();
  }, [selectedProgramId, metricType, periodCount, showDemoData, selectedOrg, activeTab, fetchProgramROI]);

  useEffect(() => {
    if (activeTab === 'cohort-trends') fetchCohortTrends();
  }, [selectedCohortId, metricType, periodCount, showDemoData, selectedOrg, activeTab, fetchCohortTrends]);

  useEffect(() => {
    if (activeTab === 'org-roi') fetchOrgROI();
  }, [showDemoData, selectedOrg, activeTab, fetchOrgROI]);
  
  const handleRefresh = () => {
    fetchData();
    if (activeTab === 'period-comparison') {
      fetchPeriodComparison();
    }
    if (activeTab === 'org-roi') {
      fetchOrgROI();
    }
    toast.success('Data refreshed');
  };
  
  const handleViewProgramDetails = (programId) => {
    // For now, just filter to that program
    setSelectedProgramId(programId);
    toast.info(`Viewing details for program`);
  };
  
  const handleViewCohortDetails = (cohortId) => {
    setSelectedCohortId(cohortId);
    toast.info(`Viewing details for cohort`);
  };
  
  // Export functions
  const [exporting, setExporting] = useState(false);
  
  const handleExport = async (type, format) => {
    setExporting(true);
    const token = secureStorage.getItem('session_token');
    
    try {
      const endpoint = type === 'program-roi' 
        ? `/api/analytics/export/program-roi/${format}`
        : `/api/analytics/export/cohort-trends/${format}`;
      
      const response = await fetch(`${API_URL}${endpoint}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (!response.ok) throw new Error(`Export failed: ${response.statusText}`);
      
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      
      const contentDisposition = response.headers.get('Content-Disposition');
      const filenameMatch = contentDisposition?.match(/filename=(.+)/);
      a.download = filenameMatch ? filenameMatch[1] : `${type}_report.${format}`;
      
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);
      
      toast.success(`${format.toUpperCase()} report downloaded successfully!`);
    } catch (err) {
      logger.error('Export error:', err);
      toast.error(`Failed to export ${format.toUpperCase()}: ${err.message}`);
    } finally {
      setExporting(false);
    }
  };
  
  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <Loader2 className="animate-spin text-indigo-600" size={48} />
      </div>
    );
  }

  const __pageTheme = getLightSectionTheme('Navy');
  const programAnalyticsHeroActions = (
    <>
      {isSuperAdmin && organizations.length > 0 && !viewAsOrg?.id && (
        <select
          value={selectedOrg}
          onChange={(e) => setSelectedOrg(e.target.value)}
          className="text-sm rounded-lg px-3 py-1.5 bg-white border border-slate-200 text-slate-800 border-0 focus:ring-2 focus:ring-white/50 min-w-[160px]"
          data-testid="analytics-org-filter"
        >
          <option value="all">All Organizations</option>
          {organizations.map(o => (
            <option key={o.id} value={o.id}>{o.name}</option>
          ))}
        </select>
      )}
      {viewAsOrg?.id && (
        <span
          className="text-sm rounded-lg px-3 py-1.5 bg-amber-50 text-amber-700 border border-amber-200 inline-flex items-center gap-1.5"
          data-testid="analytics-org-filter-locked"
          title="Org filter is locked while previewing — exit preview to widen the view."
        >
          <span className="h-1.5 w-1.5 rounded-full bg-amber-300 animate-pulse" />
          Scoped to {viewAsOrg.name || 'Preview Org'}
        </span>
      )}
      <div className="flex items-center gap-1.5 px-2.5 py-1.5 bg-white border border-slate-200 rounded-lg">
        <label
          htmlFor="analytics-demo-toggle"
          className="text-xs font-medium text-slate-700 flex items-center gap-1 cursor-pointer"
        >
          {showDemoData ? <Eye size={14} className="text-amber-700" /> : <EyeOff size={14} className="text-slate-400" />}
          Sample
        </label>
        <Switch
          id="analytics-demo-toggle"
          checked={showDemoData}
          onCheckedChange={setShowDemoData}
          data-testid="analytics-demo-toggle"
        />
      </div>
      <div className="relative group">
        <Button
          variant="outline"
          className="bg-white border border-slate-200 text-slate-900 hover:bg-white"
          disabled={exporting}
          data-testid="export-dropdown-btn"
        >
          {exporting ? (
            <Loader2 size={18} className="mr-2 animate-spin" />
          ) : (
            <Download size={18} className="mr-2" />
          )}
          Export
          <ChevronDown size={16} className="ml-1" />
        </Button>
        <div className="absolute right-0 mt-2 w-56 bg-white rounded-lg shadow-xl border opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all z-50">
          <div className="p-2">
            <p className="px-3 py-1 text-xs font-semibold text-gray-500 uppercase">Program ROI</p>
            <button
              onClick={() => handleExport('program-roi', 'excel')}
              className="w-full flex items-center gap-2 px-3 py-2 text-sm text-gray-700 hover:bg-indigo-50 rounded-md"
              data-testid="export-roi-excel"
            >
              <FileSpreadsheet size={16} className="text-emerald-600" />
              Download Excel
            </button>
            <button
              onClick={() => handleExport('program-roi', 'pdf')}
              className="w-full flex items-center gap-2 px-3 py-2 text-sm text-gray-700 hover:bg-indigo-50 rounded-md"
              data-testid="export-roi-pdf"
            >
              <FileText size={16} className="text-rose-600" />
              Download PDF
            </button>
            <hr className="my-2" />
            <p className="px-3 py-1 text-xs font-semibold text-gray-500 uppercase">Cohort Trends</p>
            <button
              onClick={() => handleExport('cohort-trends', 'excel')}
              className="w-full flex items-center gap-2 px-3 py-2 text-sm text-gray-700 hover:bg-purple-50 rounded-md"
              data-testid="export-trends-excel"
            >
              <FileSpreadsheet size={16} className="text-emerald-600" />
              Download Excel
            </button>
            <button
              onClick={() => handleExport('cohort-trends', 'pdf')}
              className="w-full flex items-center gap-2 px-3 py-2 text-sm text-gray-700 hover:bg-purple-50 rounded-md"
              data-testid="export-trends-pdf"
            >
              <FileText size={16} className="text-rose-600" />
              Download PDF
            </button>
          </div>
        </div>
      </div>
      <Button
        onClick={handleRefresh}
        variant="outline"
        className="bg-transparent text-slate-700 border-white/50 hover:bg-slate-50"
        data-testid="refresh-analytics-btn"
      >
        <RefreshCw size={18} className="mr-2" />
        Refresh
      </Button>
      <Button
        onClick={() => navigate('/segments')}
        variant="outline"
        className="bg-white border border-slate-200 text-slate-900 hover:bg-white"
      >
        <Layers size={18} className="mr-2" />
        Manage Segments
      </Button>
    </>
  );
  const __pa_total = programROIData?.total || 0;
  const __pa_withData = programROIData?.programs_with_data || 0;
  const __pa_cohorts = cohortTrendsData?.total || 0;
  const __pa_avgRoiPct = programROIData?.summary?.average_roi_percentage || 0;
  const __pa_coverage = __pa_total > 0 ? Math.round((__pa_withData / __pa_total) * 100) : 0;
  const programAnalyticsHeroStats = [
    {
      label: 'Active Navigation Initiatives',
      value: __pa_total,
      testId: 'analytics-stat-total-programs',
      whyPanel: {
        label: 'Total Programs',
        value: __pa_total,
        valueSuffix: 'programs',
        description: 'Total number of learning programs configured in your tenant — each is a separate ROI-tracked initiative tied to one or more cohorts.',
        formula: 'count(programs)',
        breakdown: [
          { label: 'How to consider it', value: '', hint: 'A growing portfolio is healthy, but tracking too many small programs dilutes attention. 8–20 active programs is a manageable sweet spot.' },
          { label: 'What to do', value: '', hint: __pa_total === 0
            ? 'No programs yet. Create your first program in Business Case Builder, then return here to monitor its ROI.'
            : 'Drill into programs below to compare ROI side-by-side and retire under-performers.' },
        ],
        links: [
          { label: 'Build a new scenario', path: '/business-case-builder' },
        ],
      },
    },
    {
      label: 'Programs With Verified Outcomes',
      value: __pa_withData,
      testId: 'analytics-stat-programs-with-data',
      whyPanel: {
        label: 'Programs with Data',
        value: __pa_withData,
        valueSuffix: `programs (${__pa_coverage}% coverage)`,
        description: 'Number of programs that have at least one imported actual KPI value — only these can produce a real (vs projected) ROI reading.',
        formula: 'count(programs WHERE has_actuals)',
        breakdown: [
          { label: 'How to consider it', value: '', hint: 'Aim for >80% data coverage. Below 50% means most of your portfolio is reporting projections, not realised value — your CFO will discount the deck.' },
          { label: 'What to do', value: '', hint: __pa_coverage < 50
            ? 'Less than half your programs have actuals. Open Data Management and import KPI history for the gaps.'
            : __pa_coverage < 80
              ? 'Coverage is acceptable but not strong. Identify which programs lack actuals and prioritise their imports this cycle.'
              : 'Strong coverage. Focus on data freshness — anything older than a quarter loses CFO credibility.' },
        ],
        links: [
          { label: 'Open Data Management', path: '/data-management' },
        ],
      },
    },
    {
      label: 'Active Cohorts',
      value: __pa_cohorts,
      testId: 'analytics-stat-cohorts',
      whyPanel: {
        label: 'Total Cohorts',
        value: __pa_cohorts,
        valueSuffix: 'cohorts',
        description: 'Total cohorts (groups of learners moving through programs together) tracked across all programs. Cohort granularity is what makes trend & comparison analysis possible.',
        formula: 'count(cohorts)',
        breakdown: [
          { label: 'How to consider it', value: '', hint: 'Multiple cohorts per program is healthy — it lets you observe drift, compare iterations, and spot which delivery waves outperformed.' },
          { label: 'What to do', value: '', hint: __pa_cohorts === 0
            ? 'No cohorts yet. Create cohorts when configuring programs to enable trend analysis.'
            : 'Use the Cohort Trends section below to compare wave-over-wave performance and lock in the patterns that worked.' },
        ],
        links: [
          { label: 'Open Comparison Analytics', path: '/comparison-analytics' },
        ],
      },
    },
    {
      label: 'Portfolio Value Conversion',
      value: `${__pa_avgRoiPct > 0 ? '+' : ''}${(__pa_avgRoiPct / 100).toFixed(2)}x`,
      testId: 'analytics-stat-avg-roi',
      whyPanel: {
        label: 'Average ROI',
        value: `${__pa_avgRoiPct > 0 ? '+' : ''}${(__pa_avgRoiPct / 100).toFixed(2)}x`,
        description: 'Mean ROI multiple across every program in your portfolio (e.g., 2.5x means each $1 of program cost returned $2.50 in tracked benefit).',
        formula: 'mean(program.roi_multiple)',
        breakdown: [
          { label: 'How to consider it', value: '', hint: 'Most enterprise L&D portfolios hover at 2x–4x. Below 1.5x suggests your programs aren\'t earning their keep; above 5x usually means projections are too optimistic — re-baseline.' },
          { label: 'What to do', value: '', hint: __pa_avgRoiPct < 150
            ? 'Average ROI is low. Drill into programs below, identify the bottom quartile, and decide: re-design or retire.'
            : __pa_avgRoiPct > 500
              ? 'Average ROI is unusually high. Re-validate assumptions in Business Case Builder — over-projecting is a fast way to lose CFO trust.'
              : 'Healthy band. Pin the top performer to your Quarterly Deck as the headline case study.' },
        ],
        links: [
          { label: 'Open Quarterly Deck', path: '/' },
          { label: 'Re-baseline projections', path: '/business-case-builder' },
        ],
      },
    },
  ];

  return (
    <PageShell theme={__pageTheme} hero={(
      <PageHero
        title="Program Navigation Portfolio"
        description="Programs are navigation initiatives. Some successfully convert capability into value; some lose value along the journey — this is where you see which is which."
        theme={__pageTheme}
        icon={TrendingUp}
        actions={programAnalyticsHeroActions}
        stats={programAnalyticsHeroStats}
        testId="program-analytics-hero"
      />
    )}>
    <div data-testid="program-analytics-page">
      <div className="px-4 md:px-8 lg:px-12 py-6">

        {/* Error Alert */}
        {error && (
          <Alert className="mb-6 border-rose-200 bg-rose-50">
            <AlertCircle className="h-4 w-4 text-rose-600" />
            <AlertDescription className="text-rose-800">{error}</AlertDescription>
          </Alert>
        )}
        
        {/* Tabs */}
        <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-6">
          <TabsList className="grid w-full grid-cols-5 h-auto p-1 bg-slate-100">
            <TabsTrigger
              value="program-roi"
              className="flex items-center gap-2 py-3 data-[state=active]:bg-white"
              data-testid="tab-program-roi"
            >
              <DollarSign className="h-4 w-4" />
              <div className="text-left">
                <div className="font-medium">Program ROI</div>
                <div className="text-xs text-slate-500">Investment vs. Impact</div>
              </div>
            </TabsTrigger>
            <TabsTrigger
              value="org-roi"
              className="flex items-center gap-2 py-3 data-[state=active]:bg-white"
              data-testid="tab-org-roi"
            >
              <Building2 className="h-4 w-4" />
              <div className="text-left">
                <div className="font-medium">Organization ROI</div>
                <div className="text-xs text-slate-500">ROI by Organization</div>
              </div>
            </TabsTrigger>
            <TabsTrigger
              value="cohort-trends"
              className="flex items-center gap-2 py-3 data-[state=active]:bg-white"
              data-testid="tab-cohort-trends"
            >
              <LineChart className="h-4 w-4" />
              <div className="text-left">
                <div className="font-medium">Cohort Trends</div>
                <div className="text-xs text-slate-500">Performance over time</div>
              </div>
            </TabsTrigger>
            <TabsTrigger
              value="period-comparison"
              className="flex items-center gap-2 py-3 data-[state=active]:bg-white"
              data-testid="tab-period-comparison"
            >
              <BarChart3 className="h-4 w-4" />
              <div className="text-left">
                <div className="font-medium">Period Comparison</div>
                <div className="text-xs text-slate-500">Quarter vs Quarter</div>
              </div>
            </TabsTrigger>
            <TabsTrigger
              value="value-pathways"
              className="flex items-center gap-2 py-3 data-[state=active]:bg-white"
              data-testid="tab-value-pathways"
            >
              <Workflow className="h-4 w-4" />
              <div className="text-left">
                <div className="font-medium">Value Pathways</div>
                <div className="text-xs text-slate-500">Capability → Value</div>
              </div>
            </TabsTrigger>
          </TabsList>
          
          {/* Program ROI Tab */}
          <TabsContent value="program-roi" className="space-y-6">
            {/* Filters */}
            <Card>
              <CardContent className="pt-6">
                <div className="flex flex-wrap items-center gap-4">
                  <div>
                    <label className="text-sm font-medium text-gray-700 mb-1 block">
                      Filter by Program
                    </label>
                    <Select value={selectedProgramId} onValueChange={setSelectedProgramId}>
                      <SelectTrigger className="w-64" data-testid="program-filter">
                        <SelectValue placeholder="All Programs" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">All Programs</SelectItem>
                        {programROIData?.programs?.map(p => (
                          <SelectItem key={p.program_id} value={p.program_id}>
                            {p.program_name}{p.organization_name && p.organization_id !== 'demo' ? ` · ${p.organization_name}` : ''}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
              </CardContent>
            </Card>
            
            {/* Summary Card */}
            {programROIData?.summary && (
              <Card className="border-indigo-200 bg-gradient-to-br from-indigo-50 to-white">
                <CardHeader>
                  <CardTitle className="flex items-center gap-2 text-indigo-800">
                    <Award className="h-5 w-5" />
                    Portfolio Summary
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
                    <div className="text-center p-4 bg-white rounded-lg border">
                      <p className="text-sm text-gray-500">Total Investment</p>
                      <p className="text-xl font-bold text-gray-900">
                        {formatCurrency(programROIData.summary.total_investment)}
                      </p>
                    </div>
                    <div className="text-center p-4 bg-white rounded-lg border">
                      <p className="text-sm text-gray-500">Total Impact</p>
                      <p className="text-xl font-bold text-gray-900">
                        {formatCurrency(programROIData.summary.total_impact)}
                      </p>
                    </div>
                    <div className="text-center p-4 bg-white rounded-lg border">
                      <p className="text-sm text-gray-500">Net Benefit</p>
                      <p className={`text-xl font-bold ${programROIData.summary.net_benefit >= 0 ? 'text-emerald-600' : 'text-rose-600'}`}>
                        {formatCurrency(programROIData.summary.net_benefit)}
                      </p>
                    </div>
                    <div className="text-center p-4 bg-white rounded-lg border">
                      <p className="text-sm text-gray-500">Average ROI</p>
                      <p className={`text-xl font-bold ${programROIData.summary.average_roi_percentage >= 0 ? 'text-emerald-600' : 'text-rose-600'}`}>
                        {programROIData.summary.average_roi_percentage >= 0 ? '+' : ''}
                        {(programROIData.summary.average_roi_percentage / 100).toFixed(2)}x
                      </p>
                    </div>
                    <div className="text-center p-4 bg-white rounded-lg border" data-testid="portfolio-conversion-efficiency">
                      <p className="text-sm text-gray-500" title="Portfolio capability → value conversion (total impact ÷ total investment)">Conversion Efficiency</p>
                      <p className="text-xl font-bold text-cyan-700">
                        {programROIData.summary.conversion_efficiency != null ? `${Math.round(programROIData.summary.conversion_efficiency)}%` : '—'}
                      </p>
                    </div>
                    <div className="text-center p-4 bg-white rounded-lg border">
                      <p className="text-sm text-gray-500">Best Performer</p>
                      <p className="text-sm font-bold text-indigo-700 truncate">
                        {programROIData.summary.best_performing_program || '—'}
                      </p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            )}

            {/* AI Portfolio Navigation Summary */}
            <PortfolioNarrativeCard programROIData={programROIData} orgId={selectedOrg} />
            
            {/* Program ROI Cards */}
            {programROIData?.programs?.length > 0 ? (
              <div className="grid gap-4">
                {programROIData.programs.map((program, index) => (
                  <ProgramROICard
                    key={program.program_id}
                    program={program}
                    rank={index + 1}
                    onViewDetails={handleViewProgramDetails}
                  />
                ))}
              </div>
            ) : (
              <Card>
                <CardContent className="py-12 text-center">
                  <DollarSign className="h-12 w-12 text-gray-300 mx-auto mb-4" />
                  <h3 className="text-lg font-medium text-gray-900 mb-2">No Program Data Available</h3>
                  <p className="text-gray-500 mb-4">
                    Import LEI component data and associate it with programs to see ROI analytics.
                  </p>
                  <div className="flex justify-center gap-3">
                    <Button onClick={() => navigate('/segments')} variant="outline">
                      Create Programs
                    </Button>
                    <Button onClick={() => navigate('/component-import')}>
                      Import Data
                    </Button>
                  </div>
                </CardContent>
              </Card>
            )}
          </TabsContent>
          
          {/* Organization ROI Tab */}
          <TabsContent value="org-roi" className="space-y-6">
            {orgROILoading ? (
              <Card>
                <CardContent className="py-12 text-center">
                  <Loader2 className="animate-spin mx-auto mb-4 text-indigo-600" size={32} />
                  <p className="text-gray-500">Loading organization ROI data...</p>
                </CardContent>
              </Card>
            ) : orgROIData?.organizations?.length > 0 ? (
              <>
                {/* Org ROI Summary */}
                <Card className="border-teal-200 bg-gradient-to-br from-teal-50 to-white">
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2 text-teal-800">
                      <Building2 className="h-5 w-5" />
                      Organization ROI Overview
                    </CardTitle>
                    <CardDescription>
                      Aggregated ROI across {orgROIData.organizations.length} organization{orgROIData.organizations.length !== 1 ? 's' : ''}
                    </CardDescription>
                  </CardHeader>
                  <CardContent>
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                      <div className="text-center p-4 bg-white rounded-lg border">
                        <p className="text-sm text-gray-500">Total Organizations</p>
                        <p className="text-2xl font-bold text-gray-900">{orgROIData.organizations.length}</p>
                      </div>
                      <div className="text-center p-4 bg-white rounded-lg border">
                        <p className="text-sm text-gray-500">Total Programs</p>
                        <p className="text-2xl font-bold text-gray-900">
                          {orgROIData.organizations.reduce((a, o) => a + o.program_count, 0)}
                        </p>
                      </div>
                      <div className="text-center p-4 bg-white rounded-lg border">
                        <p className="text-sm text-gray-500">Combined Investment</p>
                        <p className="text-xl font-bold text-gray-900">
                          {formatCurrency(orgROIData.organizations.reduce((a, o) => a + o.total_investment, 0))}
                        </p>
                      </div>
                      <div className="text-center p-4 bg-white rounded-lg border">
                        <p className="text-sm text-gray-500">Combined Net Benefit</p>
                        <p className={`text-xl font-bold ${
                          orgROIData.organizations.reduce((a, o) => a + o.net_benefit, 0) >= 0 ? 'text-emerald-600' : 'text-rose-600'
                        }`}>
                          {formatCurrency(orgROIData.organizations.reduce((a, o) => a + o.net_benefit, 0))}
                        </p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
                
                {/* Org ROI Cards */}
                <div className="grid gap-4">
                  {orgROIData.organizations.map((org, idx) => (
                    <Card
                      key={org.organization_id}
                      className={`border-l-4 hover:shadow-lg transition-shadow ${
                        org.organization_id === 'demo' ? 'bg-gradient-to-r from-amber-50/30 to-white' : ''
                      }`}
                      style={{ borderLeftColor: org.roi_percentage >= 0 ? '#10B981' : '#EF4444' }}
                      data-testid={`org-roi-card-${org.organization_id}`}
                    >
                      <CardHeader className="pb-2">
                        <div className="flex items-start justify-between">
                          <div className="flex items-center gap-2">
                            <Building2 size={18} className="text-teal-500" />
                            <CardTitle className="text-lg">{org.organization_name}</CardTitle>
                            {org.organization_id === 'demo' && (
                              <Badge className="bg-amber-100 text-amber-700 border-amber-300 text-xs">Sample</Badge>
                            )}
                          </div>
                          <ROIBadge roi={org.roi_percentage} />
                        </div>
                      </CardHeader>
                      <CardContent>
                        <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
                          <div className="text-center p-3 bg-slate-50 rounded-lg">
                            <p className="text-xs text-gray-500 uppercase tracking-wide">Programs</p>
                            <p className="text-xl font-bold text-gray-900">{org.program_count}</p>
                            <p className="text-xs text-gray-400">{org.programs_with_data} with data</p>
                          </div>
                          <div className="text-center p-3 bg-slate-50 rounded-lg">
                            <p className="text-xs text-gray-500 uppercase tracking-wide">Investment</p>
                            <p className="text-lg font-semibold text-gray-900">{formatCurrency(org.total_investment)}</p>
                          </div>
                          <div className="text-center p-3 bg-slate-50 rounded-lg">
                            <p className="text-xs text-gray-500 uppercase tracking-wide">Impact</p>
                            <p className="text-lg font-semibold text-gray-900">{formatCurrency(org.total_impact)}</p>
                          </div>
                          <div className="text-center p-3 bg-slate-50 rounded-lg">
                            <p className="text-xs text-gray-500 uppercase tracking-wide">Net Benefit</p>
                            <p className={`text-lg font-semibold ${org.net_benefit >= 0 ? 'text-emerald-600' : 'text-rose-600'}`}>
                              {org.net_benefit >= 0 ? '+' : ''}{formatCurrency(org.net_benefit)}
                            </p>
                          </div>
                          <div className="text-center p-3 bg-slate-50 rounded-lg">
                            <p className="text-xs text-gray-500 uppercase tracking-wide">ROI</p>
                            <p className={`text-2xl font-bold ${org.roi_percentage >= 0 ? 'text-emerald-600' : 'text-rose-600'}`}>
                              {org.roi_percentage > 0 ? '+' : ''}{(org.roi_percentage / 100).toFixed(2)}x
                            </p>
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              </>
            ) : (
              <Card>
                <CardContent className="py-12 text-center">
                  <Building2 className="h-12 w-12 text-gray-300 mx-auto mb-4" />
                  <h3 className="text-lg font-medium text-gray-900 mb-2">No Organization ROI Data</h3>
                  <p className="text-gray-500">Create programs and import data to see ROI by organization.</p>
                </CardContent>
              </Card>
            )}
          </TabsContent>
          
          {/* Cohort Trends Tab */}
          <TabsContent value="cohort-trends" className="space-y-6">
            {/* Filters */}
            <Card>
              <CardContent className="pt-6">
                <div className="flex flex-wrap items-center gap-4">
                  {/* Filter cohorts by Organization — only meaningful
                      when the rollup is "All Organizations" (otherwise
                      the analytics-org filter already narrows it). */}
                  {selectedOrg === 'all' && !viewAsOrg?.id
                    && (cohortTrendsData?.cohorts?.length || 0) > 6 && (
                    <div>
                      <label className="text-sm font-medium text-gray-700 mb-1 block">
                        Cohort Org
                      </label>
                      <Select value={cohortOrgFilter} onValueChange={setCohortOrgFilter}>
                        <SelectTrigger className="w-48" data-testid="cohort-org-filter">
                          <SelectValue placeholder="All Organizations" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="all">All Organizations</SelectItem>
                          {Array.from(new Map(
                            (cohortTrendsData?.cohorts || [])
                              .filter((c) => c.organization_id && c.organization_id !== 'demo')
                              .map((c) => [c.organization_id, c.organization_name || c.organization_id]),
                          ).entries()).map(([oid, oname]) => (
                            <SelectItem key={oid} value={oid}>{oname}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  )}

                  <div>
                    <label className="text-sm font-medium text-gray-700 mb-1 block">
                      Filter by Cohort
                    </label>
                    <Select value={selectedCohortId} onValueChange={setSelectedCohortId}>
                      <SelectTrigger className="w-64" data-testid="cohort-filter">
                        <SelectValue placeholder="All Cohorts" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">All Cohorts</SelectItem>
                        {(() => {
                          const all = cohortTrendsData?.cohorts || [];
                          const filtered = cohortOrgFilter === 'all'
                            ? all
                            : all.filter((c) => c.organization_id === cohortOrgFilter);
                          // Group by organization for readability when
                          // we still have many to choose from.
                          const groups = new Map();
                          filtered.forEach((c) => {
                            const key = c.organization_id || '_unspecified';
                            const label = c.organization_id === 'demo'
                              ? 'Demo'
                              : (c.organization_name || c.organization_id || 'Unspecified');
                            if (!groups.has(key)) groups.set(key, { label, items: [] });
                            groups.get(key).items.push(c);
                          });
                          if (groups.size <= 1) {
                            // Single bucket — render flat to keep the UI simple.
                            return filtered.map((c) => (
                              <SelectItem key={c.cohort_id} value={c.cohort_id}>
                                {c.cohort_name}
                              </SelectItem>
                            ));
                          }
                          // Multi-org: render org headers as disabled
                          // pseudo-items, with cohorts indented underneath.
                          const out = [];
                          Array.from(groups.entries()).forEach(([gkey, g]) => {
                            out.push(
                              <div
                                key={`hdr-${gkey}`}
                                className="px-2 pt-2 pb-0.5 text-[10px] font-bold uppercase tracking-wider text-slate-400 border-t first:border-t-0 border-slate-100"
                              >
                                {g.label}
                              </div>,
                            );
                            g.items.forEach((c) => {
                              out.push(
                                <SelectItem key={c.cohort_id} value={c.cohort_id} className="pl-4">
                                  {c.cohort_name}
                                </SelectItem>,
                              );
                            });
                          });
                          return out;
                        })()}
                      </SelectContent>
                    </Select>
                  </div>
                  
                  <div>
                    <label className="text-sm font-medium text-gray-700 mb-1 block">
                      Metric Type
                    </label>
                    <Select value={metricType} onValueChange={setMetricType}>
                      <SelectTrigger className="w-40" data-testid="metric-type-filter">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="both">Both (LEI & KPI)</SelectItem>
                        <SelectItem value="lei">LEI Scores Only</SelectItem>
                        <SelectItem value="kpi">KPIs Only</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  
                  <div>
                    <label className="text-sm font-medium text-gray-700 mb-1 block">
                      Period Count
                    </label>
                    <Select value={periodCount.toString()} onValueChange={(v) => setPeriodCount(parseInt(v))}>
                      <SelectTrigger className="w-32" data-testid="period-count-filter">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="6">Last 6</SelectItem>
                        <SelectItem value="12">Last 12</SelectItem>
                        <SelectItem value="24">Last 24</SelectItem>
                        <SelectItem value="52">Last 52</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
              </CardContent>
            </Card>
            
            {/* Cohort Trend Cards */}
            {(() => {
              const allCohorts = cohortTrendsData?.cohorts || [];
              const visibleCohorts = cohortOrgFilter === 'all'
                ? allCohorts
                : allCohorts.filter((c) => c.organization_id === cohortOrgFilter);
              if (visibleCohorts.length === 0) {
                return (
                  <Card>
                    <CardContent className="py-12 text-center">
                      <Users className="h-12 w-12 text-gray-300 mx-auto mb-4" />
                      <h3 className="text-lg font-medium text-gray-900 mb-2">No Cohort Data Available</h3>
                      <p className="text-gray-500 mb-4">
                        {cohortOrgFilter !== 'all'
                          ? 'No cohorts for the selected organization in this view. Try widening the Cohort Org filter.'
                          : 'Import LEI component data and associate it with cohorts to see performance trends.'}
                      </p>
                      <div className="flex justify-center gap-3">
                        <Button onClick={() => navigate('/segments')} variant="outline">
                          Create Cohorts
                        </Button>
                        <Button onClick={() => navigate('/component-import')}>
                          Import Data
                        </Button>
                      </div>
                    </CardContent>
                  </Card>
                );
              }
              return (
                <div className="grid md:grid-cols-2 gap-4">
                  {visibleCohorts.map((cohort) => (
                    <CohortTrendCard
                      key={cohort.cohort_id}
                      cohort={cohort}
                      onViewDetails={handleViewCohortDetails}
                    />
                  ))}
                </div>
              );
            })()}
            
            {/* Combined Trend Chart (if multiple cohorts) */}
            {cohortTrendsData?.cohorts?.length > 1 && (
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <LineChart className="h-5 w-5 text-indigo-600" />
                    Cohort Comparison
                  </CardTitle>
                  <CardDescription>
                    LEI score trends across all cohorts
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="h-80">
                    <ResponsiveContainer width="100%" height="100%">
                      <RechartsLine
                        data={
                          // Merge all cohort data by period
                          (() => {
                            const periodMap = {};
                            cohortTrendsData.cohorts.forEach((cohort, idx) => {
                              cohort.trend_data?.forEach(point => {
                                if (!periodMap[point.period]) {
                                  periodMap[point.period] = { period: point.period };
                                }
                                periodMap[point.period][cohort.cohort_name] = point.lei_score;
                              });
                            });
                            return Object.values(periodMap).sort((a, b) => a.period.localeCompare(b.period));
                          })()
                        }
                      >
                        <CartesianGrid strokeDasharray="3 3" stroke="#E5E7EB" />
                        <XAxis dataKey="period" tick={{ fontSize: 12 }} />
                        <YAxis domain={[0, 100]} tick={{ fontSize: 12 }} />
                        <Tooltip />
                        <Legend />
                        {cohortTrendsData.cohorts.map((cohort, idx) => (
                          <Line
                            key={cohort.cohort_id}
                            type="monotone"
                            dataKey={cohort.cohort_name}
                            stroke={CHART_COLORS[idx % CHART_COLORS.length]}
                            strokeWidth={2}
                            dot={{ r: 4 }}
                          />
                        ))}
                      </RechartsLine>
                    </ResponsiveContainer>
                  </div>
                </CardContent>
              </Card>
            )}
          </TabsContent>
          
          {/* Period Comparison Tab */}
          <TabsContent value="period-comparison" className="space-y-6">
            {/* Comparison Controls */}
            <Card>
              <CardContent className="pt-6">
                <div className="flex flex-wrap items-center justify-between gap-4">
                  <div className="flex flex-wrap items-center gap-4">
                    <div>
                      <label className="text-sm font-medium text-gray-700 mb-1 block">
                        Comparison Type
                      </label>
                      <Select value={comparisonType} onValueChange={setComparisonType}>
                        <SelectTrigger className="w-48" data-testid="comparison-type-select">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="quarter">Quarter vs Quarter</SelectItem>
                          <SelectItem value="month">Month vs Month</SelectItem>
                          <SelectItem value="year">Year vs Year</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    {periodComparisonData && (
                      <div className="flex items-center gap-2 px-4 py-2 bg-indigo-50 rounded-lg">
                        <Calendar size={16} className="text-indigo-600" />
                        <span className="text-sm text-indigo-900">
                          <span className="font-medium">{periodComparisonData.current_period}</span>
                          {' vs '}
                          <span className="font-medium">{periodComparisonData.previous_period}</span>
                        </span>
                      </div>
                    )}
                  </div>
                  <Button
                    variant="outline"
                    onClick={() => navigate('/alert-rules')}
                    className="flex items-center gap-2"
                    data-testid="configure-alerts-btn"
                  >
                    <BellRing size={16} />
                    Configure Alerts
                  </Button>
                </div>
              </CardContent>
            </Card>
            
            {comparisonLoading ? (
              <div className="flex items-center justify-center py-12">
                <Loader2 className="animate-spin text-indigo-600" size={32} />
              </div>
            ) : periodComparisonData?.comparisons?.length > 0 ? (
              <>
                {/* Summary Cards */}
                <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                  {(() => {
                    const withLeiData = periodComparisonData.comparisons.filter(c => c.comparison.lei_delta !== null);
                    const withKpiData = periodComparisonData.comparisons.filter(c => c.comparison.kpi_delta !== null);
                    const improved = withLeiData.filter(c => c.comparison.lei_direction === 'improved').length;
                    const declined = withLeiData.filter(c => c.comparison.lei_direction === 'declined').length;
                    const avgLeiDelta = withLeiData.length > 0 
                      ? withLeiData.reduce((sum, c) => sum + (c.comparison.lei_delta || 0), 0) / withLeiData.length 
                      : 0;
                    const avgKpiDelta = withKpiData.length > 0
                      ? withKpiData.reduce((sum, c) => sum + (c.comparison.kpi_delta || 0), 0) / withKpiData.length
                      : 0;
                    
                    return (
                      <>
                        <Card className="bg-gradient-to-br from-emerald-50 to-emerald-50 border-emerald-200">
                          <CardContent className="pt-4">
                            <div className="flex items-center gap-2 mb-2">
                              <TrendingUp className="text-emerald-600" size={20} />
                              <span className="text-sm font-medium text-emerald-800">Improved</span>
                            </div>
                            <p className="text-3xl font-bold text-emerald-700">{improved}</p>
                            <p className="text-xs text-emerald-600">cohorts with LEI gains</p>
                          </CardContent>
                        </Card>
                        <Card className="bg-gradient-to-br from-rose-50 to-rose-50 border-rose-200">
                          <CardContent className="pt-4">
                            <div className="flex items-center gap-2 mb-2">
                              <TrendingDown className="text-rose-600" size={20} />
                              <span className="text-sm font-medium text-rose-800">Declined</span>
                            </div>
                            <p className="text-3xl font-bold text-rose-700">{declined}</p>
                            <p className="text-xs text-rose-600">cohorts with LEI drops</p>
                          </CardContent>
                        </Card>
                        <Card className="bg-gradient-to-br from-indigo-50 to-purple-50 border-indigo-200">
                          <CardContent className="pt-4">
                            <div className="flex items-center gap-2 mb-2">
                              <Target className="text-indigo-600" size={20} />
                              <span className="text-sm font-medium text-indigo-800">Avg LEI Change</span>
                            </div>
                            <p className={`text-3xl font-bold ${avgLeiDelta >= 0 ? 'text-emerald-700' : 'text-rose-700'}`}>
                              {avgLeiDelta > 0 ? '+' : ''}{avgLeiDelta.toFixed(1)}
                            </p>
                            <p className="text-xs text-indigo-600">points change</p>
                          </CardContent>
                        </Card>
                        <Card className="bg-gradient-to-br from-purple-50 to-fuchsia-50 border-purple-200">
                          <CardContent className="pt-4">
                            <div className="flex items-center gap-2 mb-2">
                              <BarChart3 className="text-purple-600" size={20} />
                              <span className="text-sm font-medium text-purple-800">Avg KPI Change</span>
                            </div>
                            <p className={`text-3xl font-bold ${avgKpiDelta >= 0 ? 'text-emerald-700' : 'text-rose-700'}`}>
                              {avgKpiDelta > 0 ? '+' : ''}{avgKpiDelta.toFixed(1)}
                            </p>
                            <p className="text-xs text-purple-600">points change</p>
                          </CardContent>
                        </Card>
                      </>
                    );
                  })()}
                </div>
                
                {/* Comparison Cards Grid */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  {periodComparisonData.comparisons.map((comparison) => (
                    <PeriodComparisonCard 
                      key={comparison.cohort_id} 
                      data={comparison} 
                    />
                  ))}
                </div>
              </>
            ) : (
              <Card className="border-dashed">
                <CardContent className="py-12 text-center">
                  <BarChart3 className="mx-auto h-12 w-12 text-gray-400 mb-4" />
                  <h3 className="font-semibold text-gray-900 mb-2">No comparison data available</h3>
                  <p className="text-gray-500">
                    Enable sample data or import LEI/KPI data to see period comparisons.
                  </p>
                </CardContent>
              </Card>
            )}
          </TabsContent>

          {/* Value Pathways Tab — Missing Middle portfolio view */}
          <TabsContent value="value-pathways" className="space-y-6">
            <ValuePathwaysTab programROIData={programROIData} />
          </TabsContent>
        </Tabs>
      </div>
    </div>
    </PageShell>
  );
}
